// src/context/OrganizationContext.js

import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { doc, onSnapshot, getDoc } from 'firebase/firestore'; 
import { db, auth } from '../config/firebase-config';
import { onAuthStateChanged } from 'firebase/auth';
import { previewPersonasFor } from '../data/memberTypes';

const OrganizationContext = createContext();

export const useOrganization = () => useContext(OrganizationContext);

// Demo personas for Preview Mode. Each maps to the { role, memberType, ageTier }
// triple that resolveSurface() reads, so switching persona lands the preview on
// the matching group view. `label` is the canonical surface name; corporate-org
// relabeling for the staff personas is applied in the switcher UI.
export const PREVIEW_PERSONAS = {
    athlete: { label: 'Athlete', role: 'user',  memberType: 'athlete', ageTier: 'adult' },
    spouse:  { label: 'Spouse',  role: 'user',  memberType: 'spouse',  ageTier: 'adult' },
    teen:    { label: 'Teen',    role: 'user',  memberType: 'child',   ageTier: 'teen'  },
    kidz:    { label: 'Kidz',    role: 'user',  memberType: 'child',   ageTier: 'kid'   },
    coach:   { label: 'Coach',   role: 'coach', memberType: null,      ageTier: null    },
    exec:    { label: 'Exec',    role: 'admin', memberType: null,      ageTier: null    },
};

export const OrganizationProvider = ({ children }) => {
    const [orgData, setOrgData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [themeLoaded, setThemeLoaded] = useState(false);
    
    // --- PREVIEW MODE STATE ---
    // Preview impersonates a full member-funnel persona, not just a role: the
    // surface a user lands on is resolved from { role, memberType, ageTier }
    // (see resolveSurface). PREVIEW_PERSONAS maps each demo persona to that
    // triple so the switcher can reach every group view (athlete/spouse/teen/
    // kidz/coach/exec).
    const [isPreviewMode, setIsPreviewMode] = useState(false);
    const [previewPersona, setPreviewPersona] = useState('athlete'); // <-- which group view we're simulating
    const isPreviewRef = useRef(false); // Ref to block listener updates instantly

    // --- DEFAULTS (Matches index.css) ---
    const DEFAULT_THEME = {
        primary: '#0679af', // EAS Blue
        dark: '#0f172a',    // Slate 900
        light: '#f0f9ff',   // Sky 50
        accent: '#0ea5e9'   // Sky 500
    };

    // --- 1. RESET THEME TO DEFAULT ---
    const resetTheme = () => {
        const root = document.documentElement;
        root.style.setProperty('--brand-primary', DEFAULT_THEME.primary);
        root.style.setProperty('--brand-dark', DEFAULT_THEME.dark);
        root.style.setProperty('--brand-light', DEFAULT_THEME.light);
        root.style.setProperty('--brand-accent', DEFAULT_THEME.accent);
        // Hub-theme tokens fall back to values that are invisible in the classic
        // look, so components may reference them unconditionally.
        root.style.setProperty('--brand-highlight', DEFAULT_THEME.light);
        if (!isPreviewRef.current) setOrgData(null);
    };

    // --- 2. INJECT CUSTOM THEME ---
    const injectTheme = (brandConfig) => {
        if (!brandConfig) return resetTheme();

        const root = document.documentElement;
        if (brandConfig.primaryColor) {
            root.style.setProperty('--brand-primary', brandConfig.primaryColor);
        }
        if (brandConfig.darkColor) root.style.setProperty('--brand-dark', brandConfig.darkColor);
        if (brandConfig.lightColor) root.style.setProperty('--brand-light', brandConfig.lightColor);
        if (brandConfig.accentColor) root.style.setProperty('--brand-accent', brandConfig.accentColor);
        // Soft tinted surface for callout cards (the cream "active pledge" card in
        // the white-label mockups). Defaults to the light colour when unset.
        root.style.setProperty('--brand-highlight', brandConfig.highlightColor || brandConfig.lightColor || DEFAULT_THEME.light);
    };

    // --- 3. PREVIEW ACTIONS (NEW) ---
    const enterPreviewMode = async (orgId) => {
        setLoading(true);
        isPreviewRef.current = true;
        setIsPreviewMode(true);
        setPreviewPersona('athlete'); // Default to the Athlete surface on entry

        try {
            const orgRef = doc(db, "organizations", orgId);
            const orgSnap = await getDoc(orgRef);
            
            if (orgSnap.exists()) {
                const data = { id: orgSnap.id, ...orgSnap.data() };
                setOrgData(data);
                
                if (data.branding && data.branding.enabled) {
                    injectTheme(data.branding);
                } else {
                    resetTheme();
                }
            }
        } catch (error) {
            console.error("Preview Error:", error);
        }
        setLoading(false);
    };

    const exitPreviewMode = () => {
        isPreviewRef.current = false;
        setIsPreviewMode(false);
        setPreviewPersona('athlete'); // Reset to default surface on exit
        resetTheme();
        // The live listener stayed quiet during preview and will not fire again
        // until the user doc changes, so read the real org back explicitly: an
        // organization's own admin returns to their org, with its theme.
        const user = auth.currentUser;
        if (user) {
            setLoading(true);
            (async () => {
                try {
                    const me = await getDoc(doc(db, "users", user.uid));
                    const orgId = me.exists() ? me.data().organizationId : null;
                    if (orgId) {
                        const o = await getDoc(doc(db, "organizations", orgId));
                        if (o.exists()) {
                            const data = { id: o.id, ...o.data() };
                            setOrgData(data);
                            if (data.branding && data.branding.enabled) injectTheme(data.branding);
                        }
                    }
                } catch (e) { console.error("Preview exit:", e); }
                setLoading(false);
                setThemeLoaded(true);
            })();
        }
    };

    // Switch the previewed persona (athlete/spouse/teen/kidz/coach/exec).
    const switchPreviewPersona = (personaId) => {
        if (PREVIEW_PERSONAS[personaId]) setPreviewPersona(personaId);
    };

    // Back-compat: callers that still think in roles map onto a persona.
    const switchPreviewRole = (role) => {
        switchPreviewPersona(role === 'admin' ? 'exec' : role === 'coach' ? 'coach' : 'athlete');
    };

    // Resolve the active persona's identity triple for consumers. The minor
    // personas impersonate whichever slot the org actually puts minors in: a
    // youth-sport org has no child slot, its players under 18 are 'athlete'.
    const previewIdentity = (() => {
        const base = PREVIEW_PERSONAS[previewPersona] || PREVIEW_PERSONAS.athlete;
        const orgPersona = orgData ? previewPersonasFor(orgData).find(p => p.id === previewPersona) : null;
        return orgPersona && orgPersona.memberType !== base.memberType ? { ...base, memberType: orgPersona.memberType } : base;
    })();

    // --- 4. LOAD DATA ON AUTH CHANGE ---
    useEffect(() => {
        let userUnsub = () => {};
        let orgUnsub = () => {};

        const authUnsub = onAuthStateChanged(auth, (user) => {
            if (user) {
                // The theme is unknown again until this person's org is read: the
                // app holds its loading screen on this flag, so a member never sees
                // the default look before their organization's.
                if (!isPreviewRef.current) setThemeLoaded(false);
                userUnsub = onSnapshot(doc(db, "users", user.uid), (userSnap) => {
                    // BLOCK UPDATES IF IN PREVIEW MODE
                    if (isPreviewRef.current) return; 

                    if (userSnap.exists()) {
                        const userData = userSnap.data();
                        const orgId = userData.organizationId;

                        if (orgId) {
                            orgUnsub(); 
                            orgUnsub = onSnapshot(doc(db, "organizations", orgId), (orgSnap) => {
                                if (isPreviewRef.current) return; // Double check inside async callback

                                if (orgSnap.exists()) {
                                    const data = { id: orgSnap.id, ...orgSnap.data() };
                                    setOrgData(data);
                                    if (data.branding && data.branding.enabled) {
                                        injectTheme(data.branding);
                                    } else {
                                        resetTheme();
                                    }
                                } else {
                                    resetTheme();
                                }
                                setLoading(false);
                                setThemeLoaded(true);
                            });
                        } else {
                            orgUnsub();
                            resetTheme();
                            setLoading(false);
                            setThemeLoaded(true);
                        }
                    } else {
                        resetTheme();
                        setLoading(false);
                        setThemeLoaded(true);
                    }
                });
            } else {
                userUnsub();
                orgUnsub();
                if (!isPreviewRef.current) resetTheme();
                setLoading(false);
                setThemeLoaded(true);
            }
        });

        return () => {
            authUnsub();
            userUnsub();
            orgUnsub();
        };
        // Mount-once auth listener. injectTheme and resetTheme are plain functions
        // of this provider; listing them would re-subscribe on every render.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const value = {
        orgData,
        loading,
        themeLoaded,
        isPreviewMode,
        enterPreviewMode,
        exitPreviewMode,
        previewPersona,                            // active demo persona id
        switchPreviewPersona,                      // set the previewed group view
        previewRole: previewIdentity.role,         // derived (back-compat)
        previewMemberType: previewIdentity.memberType,
        previewAgeTier: previewIdentity.ageTier,
        switchPreviewRole                          // back-compat
    };

    return (
        <OrganizationContext.Provider value={value}>
            {children}
        </OrganizationContext.Provider>
    );
};